import { useEffect } from 'react';
import { CollapseProps } from 'antd';
import { FormikProps } from 'formik';

import { MFilter } from '@Components/Base';
import { useAppDispatch, useAppSelector } from '@Hooks/index';
import { DateRange, Input, MultiSelect, Select } from '@Components/Filters';
import {
  getCompanyOptions,
  getDepartmenProjectLabels,
  getWorkLocationOptions,
} from '@Store/selectors/dictionarySelector';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import { useGetProjectsOrDepartmentsMutation } from '@Store/api/dictionaryApiSlice';
import { dictionaryActions } from '@Store/slices/dictionarySlice';
import { getLocationSymbol } from '@Utils/getLocationSymbol';

import { FilterForm } from './types';
import {
  getAttendanceReasonLabels,
  getAttendanceStatusLabels,
  getAttendanceTechnicalIssueReasonLabels,
  getAttendanceTypeOptions,
} from '../store/selectors/attendanceRequestSelector';
import { AttendanceRequestCodes } from '../form/enum/AttendanceRequestTypeCodes.enum';

interface FiltersProps {
  formik: FormikProps<FilterForm>;
  showError?: boolean;
}

const Filters: React.FC<FiltersProps> = ({ formik, showError = false }) => {
  const dispatch = useAppDispatch();

  const user = useAppSelector(currentUserSelector);
  const attendanceTypes = useAppSelector(getAttendanceTypeOptions);
  const companies = useAppSelector(getCompanyOptions);
  const workLocationTypes = useAppSelector(getWorkLocationOptions);
  const statuses = useAppSelector(getAttendanceStatusLabels);
  const departmentProjects = useAppSelector(getDepartmenProjectLabels);
  const reasons = useAppSelector(getAttendanceReasonLabels);
  const reasonsTechnicalIssue = useAppSelector(
    getAttendanceTechnicalIssueReasonLabels
  );

  const [getProjectsDepartments] = useGetProjectsOrDepartmentsMutation();

  useEffect(() => {
    if (!user?.empNumber || !formik.values.company || !formik.values.location) {
      return;
    }

    const workLocationSymbol = getLocationSymbol(
      Number(formik.values.location)
    );

    getProjectsDepartments({
      compId: Number(formik.values.company),
      workLocation: workLocationSymbol,
      empNo: user?.empNumber,
      isAdditionalRequest: true,
    })
      .unwrap()
      .then((response) => {
        dispatch(dictionaryActions.setDepartmentsProjects(response));
      });
  }, [user?.empNumber, formik.values.company, formik.values.location]);

  const items: CollapseProps['items'] = [
    {
      id: 'REQUEST TYPE',
      label: 'Request Type',
      children: (
        <Select<FilterForm>
          formik={formik}
          formikKey="attendanceType"
          options={attendanceTypes}
        />
      ),
    },
    {
      id: 'CURRENT LOCATION',
      label: 'Current Location',
      children: (
        <Input<FilterForm> formik={formik} formikKey="currentLocation" />
      ),
    },
  ];

  if (
    formik.values.attendanceType ===
    String(AttendanceRequestCodes.ADDITIONAL_PERMANENT_ACCESS)
  ) {
    items.push(
      ...[
        {
          id: 'COMPANY',
          label: 'Company',
          children: (
            <Select<FilterForm>
              formik={formik}
              formikKey="company"
              options={companies}
            />
          ),
        },
        {
          id: 'LOCATION',
          label: 'Location',
          children: (
            <Select<FilterForm>
              formik={formik}
              formikKey="location"
              options={workLocationTypes}
            />
          ),
        },
        {
          id: 'DEPARTMENT / PROJECT',
          label: 'Department/Project',
          children: (
            <MultiSelect<FilterForm>
              formik={formik}
              formikKey="deptProject"
              options={departmentProjects}
            />
          ),
        },
      ]
    );
  }

  if (
    formik.values.attendanceType ===
    String(AttendanceRequestCodes.TEMPORARY_ACCESS)
  ) {
    items.push(
      ...[
        {
          id: 'COMPANY',
          label: 'Company',
          children: (
            <Select<FilterForm>
              formik={formik}
              formikKey="company"
              options={companies}
            />
          ),
        },
        {
          id: 'LOCATION',
          label: 'Location',
          children: (
            <Select<FilterForm>
              formik={formik}
              formikKey="location"
              options={workLocationTypes}
            />
          ),
        },
        {
          id: 'DEPARTMENT / PROJECT',
          label: 'Department/Project',
          children: (
            <MultiSelect<FilterForm>
              formik={formik}
              formikKey="deptProject"
              options={departmentProjects}
            />
          ),
        },
        {
          id: 'DATE RANGE',
          label: 'Date Range',
          children: (
            <DateRange<FilterForm>
              formik={formik}
              fromKey="dateFrom"
              toKey="dateTo"
            />
          ),
        },
      ]
    );
  }

  if (
    formik.values.attendanceType === String(AttendanceRequestCodes.NO_PUNCH) ||
    formik.values.attendanceType === String(AttendanceRequestCodes.LATE_PUNCH)
  ) {
    items.push(
      ...[
        {
          id: 'DATE RANGE',
          label: 'Date Range',
          children: (
            <DateRange<FilterForm>
              formik={formik}
              fromKey="dateFrom"
              toKey="dateTo"
            />
          ),
        },
        {
          id: 'REASON',
          label: 'Reason',
          children: (
            <MultiSelect<FilterForm>
              formik={formik}
              formikKey="reason"
              options={reasons}
            />
          ),
        },
      ]
    );
  }

  if (
    formik.values.attendanceType ===
    String(AttendanceRequestCodes.TECHNICAL_ISSUE)
  ) {
    items.push(
      ...[
        {
          id: 'COMPANY',
          label: 'Company',
          children: (
            <Select<FilterForm>
              formik={formik}
              formikKey="company"
              options={companies}
            />
          ),
        },
        {
          id: 'LOCATION',
          label: 'Location',
          children: (
            <Select<FilterForm>
              formik={formik}
              formikKey="location"
              options={workLocationTypes}
            />
          ),
        },
        {
          id: 'DEPARTMENT / PROJECT',
          label: 'Department/Project',
          children: (
            <MultiSelect<FilterForm>
              formik={formik}
              formikKey="deptProject"
              options={departmentProjects}
            />
          ),
        },
        {
          id: 'DATE RANGE',
          label: 'Date Range',
          children: (
            <DateRange<FilterForm>
              formik={formik}
              fromKey="dateFrom"
              toKey="dateTo"
            />
          ),
        },
        {
          id: 'REASON',
          label: 'Reason',
          children: (
            <MultiSelect<FilterForm>
              formik={formik}
              formikKey="reason"
              options={reasonsTechnicalIssue}
            />
          ),
        },
      ]
    );
  }

  items.push(
    ...[
      {
        id: 'STATUS',
        label: 'Status',
        children: (
          <MultiSelect<FilterForm>
            formik={formik}
            formikKey="statuses"
            options={statuses}
            showSearch
          />
        ),
      },
    ]
  );

  return (
    <MFilter<FilterForm> showError={showError} formik={formik} items={items} />
  );
};

export default Filters;
